import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    FileDown,
    FileText,
    Activity,
    Moon,
    Utensils,
    TestTube2,
    Pill,
    Calendar,
    LineChart,
    Loader2,
    Crown,
    Lock,
    Stethoscope,
    Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExportData() {
    const { user } = useAuth();
    const [exporting, setExporting] = useState(null);
    const isPremium = user?.subscription_tier === 'premium';

    const exportOptions = [
        {
            id: 'timeline',
            title: 'Medical Timeline',
            description: 'Complete health history including symptoms, sleep, appointments, and more',
            icon: FileText,
            endpoint: '/api/export/timeline',
            color: 'from-indigo-500 to-violet-500'
        },
        {
            id: 'symptoms',
            title: 'Symptom Tracker',
            description: 'All your symptom logs with severity and notes',
            icon: Activity,
            endpoint: '/api/export/tracker/symptoms',
            color: 'from-rose-500 to-pink-500'
        },
        {
            id: 'sleep',
            title: 'Sleep Tracker',
            description: 'Sleep duration, quality, and patterns',
            icon: Moon,
            endpoint: '/api/export/tracker/sleep',
            color: 'from-blue-500 to-cyan-500'
        },
        {
            id: 'food',
            title: 'Food Journal',
            description: 'Meals, foods, and dietary notes',
            icon: Utensils,
            endpoint: '/api/export/tracker/food',
            color: 'from-amber-500 to-orange-500'
        },
        {
            id: 'labs',
            title: 'Lab Results',
            description: 'Test results and values over time',
            icon: TestTube2,
            endpoint: '/api/export/tracker/labs',
            color: 'from-emerald-500 to-teal-500'
        },
        {
            id: 'supplements',
            title: 'Supplements & Meds',
            description: 'Supplement and medication tracking log',
            icon: Pill,
            endpoint: '/api/export/tracker/supplements',
            color: 'from-purple-500 to-fuchsia-500'
        },
        {
            id: 'summary-week',
            title: 'Weekly Summary',
            description: 'Overview of the past 7 days with statistics',
            icon: Calendar,
            endpoint: '/api/export/summary?period=week',
            color: 'from-sky-500 to-blue-500'
        },
        {
            id: 'summary-month',
            title: 'Monthly Summary',
            description: 'Overview of the past 30 days with statistics',
            icon: LineChart,
            endpoint: '/api/export/summary?period=month',
            color: 'from-violet-500 to-purple-500'
        },
        {
            id: 'appointments-all',
            title: 'All Appointments (with photos)',
            description: 'Every appointment with attached photos embedded inline',
            icon: Stethoscope,
            endpoint: '/api/export/appointments',
            color: 'from-sky-500 to-indigo-500'
        },
        {
            id: 'care-team-summary',
            title: 'Care Team Summary',
            description: 'Comprehensive bundle for your care team — appointments, symptoms, flares, labs, vaccines, all with photos',
            icon: Users,
            endpoint: '/api/export/care-team-summary',
            color: 'from-teal-500 to-emerald-500'
        }
    ];

    const handleExport = async (option) => {
        if (!isPremium) {
            toast.error('PDF export is a premium feature. Please upgrade to access.');
            return;
        }

        setExporting(option.id);
        try {
            const response = await axios.get(`${API_URL}${option.endpoint}`, {
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from content-disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = `${option.id}_export.pdf`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename=(.+)/);
                if (match) filename = match[1];
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(`${option.title} exported successfully!`);
        } catch (error) {
            console.error('Export failed:', error);
            if (error.response?.status === 403) {
                toast.error('PDF export is a premium feature. Please upgrade to access.');
            } else {
                toast.error('Failed to export data. Please try again.');
            }
        } finally {
            setExporting(null);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                    <FileDown className="w-8 h-8 text-indigo-500" />
                    Export Data
                </h1>
                <p className="text-muted-foreground mt-1">
                    Download your health data as PDF reports
                </p>
            </div>

            {!isPremium && (
                <Card className="bg-gradient-to-r from-indigo-500/10 to-violet-500/10 border-indigo-500/30">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Crown className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-lg">Premium Feature</h3>
                                <p className="text-sm text-muted-foreground">
                                    PDF export is available for Premium subscribers. Upgrade to download your health data and share with your healthcare providers.
                                </p>
                            </div>
                            <Button className="bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700">
                                Upgrade to Premium
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {exportOptions.map((option) => {
                    const Icon = option.icon;
                    const isExporting = exporting === option.id;
                    
                    return (
                        <Card 
                            key={option.id} 
                            className={`relative overflow-hidden transition-all ${!isPremium ? 'opacity-75' : 'hover:border-indigo-500/50'}`}
                        >
                            {!isPremium && (
                                <div className="absolute top-3 right-3">
                                    <Lock className="w-4 h-4 text-muted-foreground" />
                                </div>
                            )}
                            <CardHeader className="pb-3">
                                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${option.color} flex items-center justify-center mb-2`}>
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                <CardTitle className="text-base">{option.title}</CardTitle>
                                <CardDescription className="text-sm">
                                    {option.description}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Button
                                    onClick={() => handleExport(option)}
                                    disabled={isExporting || !isPremium}
                                    variant={isPremium ? "default" : "outline"}
                                    className="w-full gap-2"
                                >
                                    {isExporting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <FileDown className="w-4 h-4" />
                                            Download PDF
                                        </>
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Tips for Using Your Exports</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <p>
                        <strong className="text-foreground">For Doctor Visits:</strong> Export your Medical Timeline before appointments to give your healthcare provider a comprehensive view of your health patterns.
                    </p>
                    <p>
                        <strong className="text-foreground">Track Progress:</strong> Export monthly summaries to compare your health over time and identify long-term trends.
                    </p>
                    <p>
                        <strong className="text-foreground">Identify Triggers:</strong> Review your symptom and food exports together to spot potential dietary triggers.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
